/**
 * Centralized, Typed Environment Configuration
 *
 * Validates all environment variables at import time using Zod.
 * If any REQUIRED variable is missing, the app crashes immediately
 * with a clear error message instead of failing silently at runtime.
 *
 * Usage:
 *   import { env } from '@/lib/env'
 *   env.database.postgresUrl  // string (guaranteed)
 *   env.redis.url             // string | undefined (optional)
 *
 * Platform-agnostic: works the same on Vercel, PM2, Docker, or serverless.
 * All platforms inject into process.env — this module just validates it.
 */

import { z } from "zod";

// ────────────────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────────────────

/** Parse string to positive integer, returning default if invalid/missing */
const positiveInt = (defaultValue: number) =>
    z
        .string()
        .optional()
        .transform((v) => {
            const n = parseInt(v ?? "", 10);
            return Number.isNaN(n) || n < 0 ? defaultValue : n;
        });

/** Boolean from string: "true" → true, everything else → false */
const booleanString = (defaultValue: boolean) =>
    z
        .string()
        .optional()
        .transform((v) => {
            if (v === undefined) return defaultValue;
            return v === "true";
        });

/** Enum-like string with a default */
const stringWithDefault = (defaultValue: string) =>
    z
        .string()
        .optional()
        .transform((v) => v || defaultValue);

// ────────────────────────────────────────────────────────
// Schema: Server-Side Variables
// ────────────────────────────────────────────────────────

const serverSchema = z.object({
    // ─── Database ───
    DATABASE_URL_POSTGRES: z.string().min(1, "DATABASE_URL_POSTGRES is required"),
    DATABASE_URL: z.string().optional(),
    DATABASE_URL_MYSQL: z.string().optional(),
    DATABASE_POOL_SIZE: positiveInt(10),
    SSL_CERT: z.string().optional(),

    // ─── Redis ───
    DATABASE_URL_REDIS: z.string().optional(),
    REDIS_HOST: stringWithDefault("127.0.0.1"),
    REDIS_PORT: positiveInt(6379),
    REDIS_PASSWORD: z.string().optional(),

    // ─── Authentication ───
    JWT_SECRET: z.string().min(1, "JWT_SECRET is required — generate with: openssl rand -base64 32"),
    AUTH_SECRET: z.string().optional(),
    SESSION_SECRET: z.string().optional(),
    NEXTAUTH_SECRET: z.string().optional(),
    NEXTAUTH_URL: z.string().optional(),
    AUTH_COOKIE_NAME: stringWithDefault("ibpersonnel.session"),
    AUTH_TOKEN_TTL: positiveInt(86400),
    AUTH_COOKIE_MAX_AGE: positiveInt(86400),

    // ─── Active Directory ───
    AD_URL: z.string().optional(),
    AD_BASE_DN: z.string().optional(),
    AD_USERNAME: z.string().optional(),
    AD_PASSWORD: z.string().optional(),

    // ─── Two-Factor Auth ───
    AUTH_TWO_FACTOR_ENABLED: booleanString(false),
    AUTH_TWO_FACTOR_ISSUER: z.string().optional(),
    AUTH_TWO_FACTOR_LABEL: z.string().optional(),
    AUTH_TWO_FACTOR_DIGITS: positiveInt(6),
    AUTH_TWO_FACTOR_PERIOD: positiveInt(30),
    AUTH_TWO_FACTOR_WINDOW: positiveInt(1),
    AUTH_TWO_FACTOR_RECOVERY_CODES: positiveInt(10),
    AUTH_TWO_FACTOR_SECRET_BYTES: positiveInt(20),
    AUTH_TWO_FACTOR_CHALLENGE_TTL: positiveInt(300),

    // ─── File Storage ───
    FILE_STORAGE_PROVIDER: z
        .enum(["local", "cloudinary"])
        .optional()
        .default("cloudinary"),
    FILE_STORAGE_PATH: stringWithDefault("/var/uploads/ibpersonnel"),
    FILE_STORAGE_URL: stringWithDefault("http://localhost:3000/api/uploads"),
    FILE_ENCRYPTION_KEY: z.string().optional(),
    MAX_FILE_SIZE: positiveInt(50 * 1024 * 1024),
    STORAGE_CACHE_TTL: positiveInt(3600),
    FILE_SHARING_SERVICES: stringWithDefault("s3,firebase,fileio,tmpfiles,local"),
    UPLOADTHING_SECRET: z.string().optional(),
    UPLOADTHING_APP_ID: z.string().optional(),

    // ─── Cloudinary ───
    CLOUDINARY_CLOUD_NAME: z.string().optional(),
    CLOUDINARY_API_KEY: z.string().optional(),
    CLOUDINARY_API_SECRET: z.string().optional(),

    // ─── Email ───
    EMAIL_PROVIDER: stringWithDefault("zeptomail"),
    EMAIL_FROM: z.string().optional(),
    ZEPTOMAIL_API_KEY: z.string().optional(),
    ZEPTOMAIL_TOKEN: z.string().optional(),
    ZEPTOMAIL_API_URL: stringWithDefault("https://api.zeptomail.com/v1.1/email"),
    ZEPTOMAIL_FROM_EMAIL: stringWithDefault("noreply@infobytesolution.com"),
    ZEPTOMAIL_FROM_NAME: stringWithDefault("NAMA Personnel System"),
    RESEND_API_KEY: z.string().optional(),
    EMAIL_MAX_RETRIES: positiveInt(3),
    EMAIL_RETRY_DELAY_MS: positiveInt(1000),
    EMAIL_RATE_LIMIT_PER_MINUTE: positiveInt(100),

    // ─── AI ───
    GOOGLE_AI_API_KEY: z.string().optional(),

    // ─── External Services ───
    FINGERPRINT_SERVICE_URL: stringWithDefault("http://localhost:5050/api/fingerprint"),
    FACE_VERIFY_URL: stringWithDefault("http://localhost:5051"),
    UPSTASH_QSTASH_TOKEN: z.string().optional(),

    // ─── Queue / Worker ───
    QUEUE_PROVIDER: stringWithDefault("bullmq"),
    BULLMQ_WORKER_ENABLED: booleanString(false),
    WORKER_CONCURRENCY: positiveInt(50),

    // ─── Feature Flags ───
    ENABLE_AUDIT_LOGS: booleanString(true),
    ENABLE_AUDIT_LOGGING: booleanString(true),
    ENABLE_SERVER_LOGGING: booleanString(false),

    // ─── Application ───
    NODE_ENV: stringWithDefault("development"),
    PORT: positiveInt(3000),
    HOSTNAME: stringWithDefault("0.0.0.0"),
});

// ────────────────────────────────────────────────────────
// Schema: Client-Side Variables (NEXT_PUBLIC_*)
// ────────────────────────────────────────────────────────

const clientSchema = z.object({
    NEXT_PUBLIC_APP_URL: stringWithDefault("http://localhost:3000"),
    NEXT_PUBLIC_NEXTAUTH_URL: stringWithDefault("http://localhost:3000"),
    NEXT_PUBLIC_APP_NAME: stringWithDefault("IBPersonnel"),
    NEXT_PUBLIC_BRAND_COLOR: stringWithDefault("#0066cc"),
    NEXT_PUBLIC_ENABLE_FINGERPRINT_V2: booleanString(false),
    NEXT_PUBLIC_BUILD_ID: z.string().optional(),
    NEXT_PUBLIC_GIT_COMMIT: z.string().optional(),
    NEXT_PUBLIC_BUILD_DATE: z.string().optional(),
    NEXT_PUBLIC_SAFE_REACT: stringWithDefault("0"),
    NEXT_PUBLIC_SAFE_REACT_LEVEL: z.string().optional(),
    NEXT_PUBLIC_SAFE_REACT_LOG_LEVEL: stringWithDefault("warn"),
    NEXT_PUBLIC_SAFE_REACT_LIMIT: positiveInt(500),
    NEXT_PUBLIC_SAFE_REACT_MIN_MS: positiveInt(0),
    NEXT_PUBLIC_SAFE_REACT_MAX_PER_FRAME: positiveInt(0),
    NEXT_PUBLIC_SAFE_REACT_EWMA_ALPHA: z.string().optional(),
    NEXT_PUBLIC_SAFE_REACT_ANOMALY_Z: z.string().optional(),
    NEXT_PUBLIC_SAFE_REACT_WARMUP_SAMPLES: positiveInt(8),
    NEXT_PUBLIC_SAFE_REACT_COLD_MAX_RATE: positiveInt(300),
    NEXT_PUBLIC_SAFE_REACT_COLD_MIN_GAP_MS: positiveInt(2),
    NEXT_PUBLIC_SAFE_REACT_QUARANTINE_BASE_MS: positiveInt(16),
    NEXT_PUBLIC_SAFE_REACT_QUARANTINE_MAX_MS: positiveInt(256),
    NEXT_PUBLIC_SAFE_REACT_PINGPONG_WINDOW: positiveInt(6),

    // ─── Branding ───
    NEXT_PUBLIC_BRAND_NAME: stringWithDefault("IBSuite"),
    NEXT_PUBLIC_BRAND_SHORT_NAME: z.string().optional(),
    NEXT_PUBLIC_BRAND_ORGANIZATION: stringWithDefault("Infobyte Solution"),
    NEXT_PUBLIC_BRAND_LOGO: stringWithDefault("/images/logo.png"),
    NEXT_PUBLIC_BRAND_LOGO_LIGHT: z.string().optional(),
    NEXT_PUBLIC_BRAND_LOGO_DARK: z.string().optional(),
    NEXT_PUBLIC_BRAND_FAVICON: stringWithDefault("/images/logo.png"),
    NEXT_PUBLIC_BRAND_STATION_NAME: stringWithDefault("Station"),
    NEXT_PUBLIC_BRAND_STATION_PLURAL: stringWithDefault("Stations"),
    NEXT_PUBLIC_BRAND_STATION_GROUP_NAME: stringWithDefault("Station Group"),
    NEXT_PUBLIC_BRAND_STATION_GROUP_PLURAL: stringWithDefault("Station Groups"),
    NEXT_PUBLIC_BRAND_RANK_NAME: stringWithDefault("Designation"),
    NEXT_PUBLIC_BRAND_RANK_PLURAL: stringWithDefault("Designations"),

    // ─── Module Feature Flags ───
    NEXT_PUBLIC_ENABLE_PAYROLL: booleanString(true),
    NEXT_PUBLIC_ENABLE_PERSONNEL: booleanString(true),
});

// ────────────────────────────────────────────────────────
// Validation & Export
// ────────────────────────────────────────────────────────

/**
 * Is this code running on the server or in the browser?
 * On the server we validate everything. In the browser we only validate NEXT_PUBLIC_*.
 */
const isServer = typeof window === "undefined";

function validateEnv() {
    // Client-side variables (always validated — they're in the bundle)
    const clientResult = clientSchema.safeParse(process.env);
    if (!clientResult.success) {
        const formatted = clientResult.error.issues
            .map((i) => `  ✗ ${i.path.join(".")}: ${i.message}`)
            .join("\n");
        throw new Error(
            `\n❌ Invalid client environment variables:\n${formatted}\n\nSee .env.example for documentation.\n`
        );
    }

    // Server-side variables (only validated on the server)
    if (isServer) {
        const skipValidation = process.env.SKIP_ENV_VALIDATION === "true" || process.env.NEXT_PHASE === "phase-production-build";
        
        const serverResult = serverSchema.safeParse(process.env);
        if (!serverResult.success && !skipValidation) {
            const formatted = serverResult.error.issues
                .map((i) => `  ✗ ${i.path.join(".")}: ${i.message}`)
                .join("\n");
            throw new Error(
                `\n❌ Invalid server environment variables:\n${formatted}\n\nSee .env.example for documentation.\n`
            );
        }

        const data = serverResult.success ? serverResult.data : ({} as any);

        return {
            // ─── Structured access for common patterns ───
            database: {
                postgresUrl: data.DATABASE_URL_POSTGRES,
                url: data.DATABASE_URL,
                mysqlUrl: data.DATABASE_URL_MYSQL,
                poolSize: data.DATABASE_POOL_SIZE ?? 10,
                sslCert: data.SSL_CERT,
            },
            redis: {
                url: data.DATABASE_URL_REDIS,
                host: data.REDIS_HOST ?? "127.0.0.1",
                port: data.REDIS_PORT ?? 6379,
                password: data.REDIS_PASSWORD,
            },
            auth: {
                jwtSecret: data.JWT_SECRET,
                authSecret: data.AUTH_SECRET,
                sessionSecret: data.SESSION_SECRET,
                nextauthSecret: data.NEXTAUTH_SECRET,
                nextauthUrl: data.NEXTAUTH_URL,
                cookieName: data.AUTH_COOKIE_NAME ?? "ibpersonnel.session",
                tokenTtl: data.AUTH_TOKEN_TTL ?? 86400,
                cookieMaxAge: data.AUTH_COOKIE_MAX_AGE ?? 86400,
            },
            ad: {
                url: data.AD_URL,
                baseDn: data.AD_BASE_DN,
                username: data.AD_USERNAME,
                password: data.AD_PASSWORD,
            },
            twoFactor: {
                enabled: data.AUTH_TWO_FACTOR_ENABLED ?? false,
                issuer: data.AUTH_TWO_FACTOR_ISSUER,
                label: data.AUTH_TWO_FACTOR_LABEL,
                digits: data.AUTH_TWO_FACTOR_DIGITS ?? 6,
                period: data.AUTH_TWO_FACTOR_PERIOD ?? 30,
                window: data.AUTH_TWO_FACTOR_WINDOW ?? 1,
                recoveryCodes: data.AUTH_TWO_FACTOR_RECOVERY_CODES ?? 10,
                secretBytes: data.AUTH_TWO_FACTOR_SECRET_BYTES ?? 20,
                challengeTtl: data.AUTH_TWO_FACTOR_CHALLENGE_TTL ?? 300,
            },
            storage: {
                provider: data.FILE_STORAGE_PROVIDER ?? "cloudinary",
                localPath: data.FILE_STORAGE_PATH ?? "/var/uploads/ibpersonnel",
                localUrl: data.FILE_STORAGE_URL ?? "http://localhost:3000/api/uploads",
                encryptionKey: data.FILE_ENCRYPTION_KEY,
                maxFileSize: data.MAX_FILE_SIZE ?? (50 * 1024 * 1024),
                cacheTtl: data.STORAGE_CACHE_TTL ?? 3600,
                sharingServices: data.FILE_SHARING_SERVICES ?? "s3,firebase,fileio,tmpfiles,local",
                uploadthingSecret: data.UPLOADTHING_SECRET,
                uploadthingAppId: data.UPLOADTHING_APP_ID,
            },
            cloudinary: {
                cloudName: data.CLOUDINARY_CLOUD_NAME,
                apiKey: data.CLOUDINARY_API_KEY,
                apiSecret: data.CLOUDINARY_API_SECRET,
            },
            email: {
                provider: data.EMAIL_PROVIDER ?? "zeptomail",
                from: data.EMAIL_FROM,
                zeptomailApiKey: data.ZEPTOMAIL_API_KEY,
                zeptomailToken: data.ZEPTOMAIL_TOKEN,
                zeptomailApiUrl: data.ZEPTOMAIL_API_URL ?? "https://api.zeptomail.com/v1.1/email",
                zeptomailFromEmail: data.ZEPTOMAIL_FROM_EMAIL ?? "noreply@infobytesolution.com",
                zeptomailFromName: data.ZEPTOMAIL_FROM_NAME ?? "NAMA Personnel System",
                resendApiKey: data.RESEND_API_KEY,
                maxRetries: data.EMAIL_MAX_RETRIES ?? 3,
                retryDelayMs: data.EMAIL_RETRY_DELAY_MS ?? 1000,
                rateLimitPerMinute: data.EMAIL_RATE_LIMIT_PER_MINUTE ?? 100,
            },
            ai: {
                googleApiKey: data.GOOGLE_AI_API_KEY,
            },
            services: {
                fingerprintUrl: data.FINGERPRINT_SERVICE_URL ?? "http://localhost:5050/api/fingerprint",
                faceVerifyUrl: data.FACE_VERIFY_URL ?? "http://localhost:5051",
                upstashQstashToken: data.UPSTASH_QSTASH_TOKEN,
            },
            queue: {
                provider: data.QUEUE_PROVIDER ?? "bullmq",
                bullmqWorkerEnabled: data.BULLMQ_WORKER_ENABLED ?? false,
                workerConcurrency: data.WORKER_CONCURRENCY ?? 50,
            },
            features: {
                auditLogs: data.ENABLE_AUDIT_LOGS ?? true,
                auditLogging: data.ENABLE_AUDIT_LOGGING ?? true,
                serverLogging: data.ENABLE_SERVER_LOGGING ?? false,
            },
            app: {
                nodeEnv: data.NODE_ENV ?? "development",
                port: data.PORT ?? 3000,
                hostname: data.HOSTNAME ?? "0.0.0.0",
                isProduction: data.NODE_ENV === "production",
                isDevelopment: data.NODE_ENV === "development",
            },

            // Client vars are also available on the server
            client: {
                ...clientResult.data,
                modules: {
                    payroll: clientResult.data.NEXT_PUBLIC_ENABLE_PAYROLL,
                    personnel: clientResult.data.NEXT_PUBLIC_ENABLE_PERSONNEL,
                },
            },
        };
    }

    // Client-side: only public vars are available
    return {
        database: {} as never,
        redis: {} as never,
        auth: {} as never,
        ad: {} as never,
        twoFactor: {} as never,
        storage: {} as never,
        cloudinary: {} as never,
        email: {} as never,
        ai: {} as never,
        services: {} as never,
        queue: {} as never,
        features: {} as never,
        app: {} as never,
        client: {
            ...clientResult.data,
            modules: {
                payroll: clientResult.data.NEXT_PUBLIC_ENABLE_PAYROLL,
                personnel: clientResult.data.NEXT_PUBLIC_ENABLE_PERSONNEL,
            },
        },
    };
}

/**
 * Validated, typed environment configuration.
 *
 * Server-side: all properties are populated on first access.
 * Client-side: only `env.client.*` (NEXT_PUBLIC_*) properties are available.
 *
 * If any required variable is missing, the process crashes immediately
 * with a descriptive error listing exactly which variables are invalid.
 */
export const env = validateEnv();

/** Re-export the type for use in other modules */
export type Env = typeof env;
